import { useCallback, useId, useMemo, useState } from 'react';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    Label,
    RadioGroup,
    RadioGroupSimpleItem,
} from '@/ui/design-system/components';
import { searchHashtags } from 'utils/api/hashtags.api';
import WidgetEntityMultiSelect from './WidgetEntityMultiSelect';
import WidgetHashtagMultiSelect from './WidgetHashtagMultiSelect';
import WidgetExcludeInput from './WidgetExcludeInput';
import { createProxySearchClient, searchEntities } from './entitySearch';

const EMPTY = [];

const NOUN_PLURAL = {
    brand: 'brands',
    category: 'categories',
    hashtag: 'hashtags',
};

/**
 * Mode is local state, NOT derived from the args: "specific list, nothing
 * picked yet" is a real state, and deriving it from `entity_ids` would snap the
 * user back to "top results" the moment they opened the picker. It does resync
 * when replaced args name a side: Describe swaps `draftArgs` without
 * remounting, and a stale mode writes the pins+exclude pair.
 *
 * Pins and `exclude` are mutually exclusive — the backend 400s on the pair.
 */
function WidgetRowsSource({ type, draftArgs, setArg, dataSource, geo }) {
    const pinKey = type.controls.includes('hashtags')
        ? 'hashtags'
        : type.controls.includes('entity_ids')
          ? 'entity_ids'
          : null;
    const canExclude = type.controls.includes('exclude');

    const pins = draftArgs[pinKey] ?? EMPTY;
    const exclusions = draftArgs.exclude ?? EMPTY;

    const [mode, setMode] = useState(() => (pins.length ? 'list' : 'top'));
    const [pendingMode, setPendingMode] = useState(null);
    const argsMode = pins.length ? 'list' : exclusions.length ? 'top' : null;
    const [lastArgsMode, setLastArgsMode] = useState(argsMode);
    if (argsMode !== lastArgsMode) {
        // Emptying a side is tracked but does not move the mode, so clearing
        // the picker leaves you in it — and a later regeneration of that same
        // side still reads as a change, rather than matching a stale value.
        setLastArgsMode(argsMode);
        if (argsMode) setMode(argsMode);
    }
    const groupId = useId();
    const nounPlural = NOUN_PLURAL[type.pinnedEntity] ?? 'rows';

    const client = useMemo(
        () => (dataSource === 'pop_index' ? createProxySearchClient() : null),
        [dataSource],
    );
    const entityTypes = useMemo(
        () => (type.pinnedEntity ? [type.pinnedEntity] : null),
        [type.pinnedEntity],
    );
    const excludeDepths = type.pinnedDepthsExcluded;

    const searchNames = useCallback(
        async (query) => {
            if (client && entityTypes) {
                const hits = await searchEntities({
                    client,
                    query,
                    types: entityTypes,
                    excludeDepths,
                });
                return hits.map((hit) => hit.name).filter(Boolean);
            }
            if (dataSource === 'tiktok') {
                // `{ data: [...] }`, not a bare array — a wrong unwrap
                // returns nothing, silently.
                const found = await searchHashtags({ query, geo, limit: 8 });
                return (found?.data ?? [])
                    .map((hit) => hit.hashtag_name)
                    .filter(Boolean);
            }
            return [];
        },
        [client, entityTypes, excludeDepths, dataSource, geo],
    );
    const hasSearch = Boolean(
        (client && entityTypes) || dataSource === 'tiktok',
    );

    const applyMode = (next) => {
        setMode(next);
        if (next === 'list') setArg('exclude', undefined);
        else setArg(pinKey, undefined);
    };

    const requestMode = (next) => {
        if (next === mode) return;
        const losing = next === 'list' ? exclusions : pins;
        if (losing.length) {
            setPendingMode(next);
            return;
        }
        applyMode(next);
    };

    const renderPicker = () =>
        pinKey === 'hashtags' ? (
            <WidgetHashtagMultiSelect
                value={pins}
                geo={geo}
                onChange={(names) => setArg('hashtags', names)}
            />
        ) : (
            <WidgetEntityMultiSelect
                value={pins}
                pinnedKind={type.pinnedEntity}
                dataSource={dataSource}
                excludeDepths={excludeDepths}
                required={type.entity === 'none'}
                onChange={(ids) => setArg('entity_ids', ids)}
            />
        );

    const renderExclude = () => (
        <WidgetExcludeInput
            value={exclusions}
            nounPlural={nounPlural}
            searchNames={hasSearch ? searchNames : undefined}
            onChange={(terms) => setArg('exclude', terms)}
        />
    );

    if (!pinKey) return canExclude ? renderExclude() : null;
    if (!canExclude) return renderPicker();

    const losingCount =
        pendingMode === 'list' ? exclusions.length : pins.length;

    return (
        <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-2">
                {/* A group heading, not a <label>: a label names one control,
                    and a radio group is named by aria-labelledby. */}
                <span
                    id={`${groupId}-label`}
                    className="text-sm font-medium text-foreground"
                >
                    Rows to show
                </span>
                <RadioGroup
                    aria-labelledby={`${groupId}-label`}
                    value={mode}
                    onValueChange={requestMode}
                    className="flex gap-4"
                >
                    {[
                        { value: 'top', label: 'Top results' },
                        { value: 'list', label: `Specific ${nounPlural}` },
                    ].map((option) => (
                        <div
                            key={option.value}
                            className="flex items-center gap-2"
                        >
                            <RadioGroupSimpleItem
                                value={option.value}
                                id={`${groupId}-${option.value}`}
                            />
                            <Label
                                htmlFor={`${groupId}-${option.value}`}
                                className="text-sm font-normal cursor-pointer capitalize"
                            >
                                {option.label}
                            </Label>
                        </div>
                    ))}
                </RadioGroup>
            </div>
            {mode === 'list' ? renderPicker() : renderExclude()}

            <AlertDialog
                open={pendingMode !== null}
                onOpenChange={(next) => {
                    if (!next) setPendingMode(null);
                }}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            {pendingMode === 'list'
                                ? 'Show a specific list?'
                                : 'Show top results?'}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            {pendingMode === 'list'
                                ? `This removes your ${losingCount} exclusion${
                                      losingCount === 1 ? '' : 's'
                                  }. A specific list already names every row, so the two can't be combined.`
                                : `This removes the ${losingCount} ${nounPlural} you picked, and shows the top results instead.`}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => {
                                applyMode(pendingMode);
                                setPendingMode(null);
                            }}
                        >
                            Continue
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

export default WidgetRowsSource;
